import type { ApprovalAttributes } from '@/types/approval';

/** 마케팅 비용 품의 — 레거시 appr_edit_forms_marketing_ai */
export const MARKETING_EXPENSE_TYPE = 212;

export const isMarketingExpenseType = (apvType: number): boolean =>
  apvType === MARKETING_EXPENSE_TYPE;

/** 마케팅 분석 레코드(draft-source display.source) — 레거시 getPortalLabsData 응답 data 와 같은 모양 */
export type MarketingAnalysis = {
  id?: string;
  result?: Record<string, unknown> | null;
  [key: string]: unknown;
};

export type MarketingExpenseState = {
  analysisId: string;
  /** 조회 전·실패면 null */
  analysis: MarketingAnalysis | null;
};

export type MarketingCategory = 'common' | 'category' | 'etc';

/** 숫자로 못 읽는 값(`-` 등)은 받은 글자 그대로 */
export type MarketingAmount = number | string;

export type MarketingBudgetRow = {
  account: string;
  company: string;
  note: string;
  cost: MarketingAmount;
};

export type MarketingBudgetGroup = {
  name: string;
  total: MarketingAmount;
  rows: MarketingBudgetRow[];
};

export type MarketingBudgetStatusItem = {
  amount: MarketingAmount;
  percent: number;
};

export type MarketingSapUsage = {
  name: string;
  total: MarketingAmount;
  curr: MarketingAmount;
  percentText: string;
};

export type MarketingBudgetCheckRow = {
  type: string;
  budgetTotal: MarketingAmount;
  remain: MarketingAmount;
  currentUsage: MarketingAmount;
  proceedPercent: number;
};

/** **굵게** 표시 조각 — 레거시는 <strong> 로 바꿔 넣음 */
export type MarketingSummarySegment = { text: string; bold: boolean };

export type MarketingExpenseView = {
  brand: string;
  season: string;
  isKids: boolean;
  startDate: string;
  endDate: string;
  /** 알려진 값이 아니면 원문 그대로 */
  category: MarketingCategory | string;
  categoryItemCodes: string[];
  /** null 이면 「(기안 목적 미기재)」 */
  purpose: string | null;
  summary: string;
  /** threeLineSummary 가 없으면 null — 레거시는 요약·상세 둘 다 비워 둠 */
  brief: MarketingSummarySegment[][] | null;
  detailHtml: string;
  totalCost: number;
  groups: MarketingBudgetGroup[];
  status: {
    used: MarketingBudgetStatusItem;
    current: MarketingBudgetStatusItem;
    remain: MarketingBudgetStatusItem;
  };
  sapUsages: MarketingSapUsage[];
  budgetChecks: MarketingBudgetCheckRow[];
};

type Rec = Record<string, unknown>;

const asRecord = (v: unknown): Rec | null =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Rec) : null;

const asText = (v: unknown): string =>
  typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '';

const asArray = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);

/** 레거시 `x || 0` / `Number(x ?? 0)` — 숫자로 못 읽으면 0. 문자 값은 쉼표가 있어도 숫자로 읽음 */
const toNumber = (v: unknown): number => {
  const n = Number(typeof v === 'string' ? v.replace(/,/g, '') : v ?? 0);
  return Number.isFinite(n) ? n : 0;
};

/** 금액 표시값 — 숫자면 숫자(쉼표 표기용), 숫자로 못 읽는 글자는 그대로, 비면 0 */
const toAmount = (v: unknown): MarketingAmount => {
  if (v === null || v === undefined || v === '') return 0;
  if (typeof v === 'string') {
    const n = Number(v.replace(/,/g, ''));
    return v.trim() && Number.isFinite(n) ? n : v.trim();
  }
  return toNumber(v);
};

/** 레거시 toISOString().split('T')[0] — UTC 날짜(221 과 같음) */
const toLegacyDate = (v: unknown): string => {
  if (typeof v !== 'string' && typeof v !== 'number') return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
};

/** 레거시 displayBudgetStatus fmtPct */
export const formatStatusPercent = (v: number): string =>
  `${(Math.floor(v * 100) % 100 === 0 ? v.toFixed(0) : v.toFixed(2))
    .replace(/\.00$/, '')
    .replace(/(\.\d)0$/, '$1')}%`;

/** 레거시 displayBudgetUsage pctText */
const formatUsagePercent = (v: number): string =>
  Number.isFinite(v)
    ? `${v
        .toFixed(2)
        .replace(/\.00$/, '')
        .replace(/(\.\d)0$/, '$1')}%`
    : '0%';

/** 레거시 순서 — **굵게** 치환 뒤 맨 앞 `* ` 제거 */
const toBriefSegments = (line: string): MarketingSummarySegment[] => {
  const trimmed = line.trim();
  const segments: MarketingSummarySegment[] = [];
  const re = /\*\*([^*]+)\*\*/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(trimmed))) {
    if (m.index > last)
      segments.push({ text: trimmed.slice(last, m.index), bold: false });
    segments.push({ text: m[1], bold: true });
    last = m.index + m[0].length;
  }
  if (last < trimmed.length)
    segments.push({ text: trimmed.slice(last), bold: false });
  if (segments[0] && !segments[0].bold) {
    segments[0].text = segments[0].text.replace(/^\*\s*/, '');
    if (!segments[0].text) segments.shift();
  }
  return segments;
};

const statusPercent = (v: unknown): number =>
  typeof v === 'number' && Number.isFinite(v) ? v : 0;

export const toMarketingExpenseView = (
  analysis: MarketingAnalysis | null | undefined,
): MarketingExpenseView | null => {
  const result = asRecord(analysis?.result);
  const value = asRecord(result?.value);
  if (!result || !value) return null;

  const range = asArray(value.dateRange);
  const hasRange = range.length >= 2;
  const purpose = asText(value.purpose);

  const groups: MarketingBudgetGroup[] = Object.entries(
    asRecord(result.sapGroups) ?? {},
  ).map(([key, raw]) => {
    const group = asRecord(raw) ?? {};
    return {
      name: asText(group.sapAccount) || key,
      total: toAmount(group.totalCost),
      rows: asArray(group.rows).map((r) => {
        const row = asRecord(r) ?? {};
        return {
          account: asText(row.dtlAccount) || asText(row.sapAccount),
          company: asText(row.company),
          note: asText(row.note),
          cost: toAmount(row.cost),
        };
      }),
    };
  });

  const threeLineSummary = asText(value.threeLineSummary);
  const brief = threeLineSummary
    ? threeLineSummary
        .split('\n')
        .filter((line) => line.trim() !== '' && !line.startsWith('네,'))
        .map(toBriefSegments)
        .filter((segments) => segments.length > 0)
    : null;

  const status = asRecord(result.budgetStatus) ?? {};

  const groupsRaw = Object.values(asRecord(result.sapGroups) ?? {}).map(
    (g) => asRecord(g) ?? {},
  );

  return {
    brand: asText(value.brand),
    season: asText(value.season),
    isKids: value.kidSection === true,
    startDate: hasRange ? toLegacyDate(range[0]) : '',
    endDate: hasRange ? toLegacyDate(range[1]) : '',
    category: asText(value.category),
    categoryItemCodes: asArray(value.categoryItemCodes).map(asText),
    purpose: purpose.trim() ? purpose : null,
    summary:
      asText(value.content) ||
      asText(value.summary) ||
      asText(value.description),
    brief,
    detailHtml: brief
      ? `${asText(value.analysis0HTML)}\n\n---\n\n${asText(
          value.analysis1HTML,
        )}`
      : '',
    totalCost: groups.reduce(
      (sum, g) => sum + g.rows.reduce((s, r) => s + toNumber(r.cost), 0),
      0,
    ),
    groups,
    status: {
      used: {
        amount: toAmount(status.useBudget),
        percent: statusPercent(status.useBudgetPercent),
      },
      current: {
        amount: toAmount(status.currentBudget),
        percent: statusPercent(status.currentBudgetPercent),
      },
      remain: {
        amount: toAmount(status.remainBudget),
        percent: statusPercent(status.remainBudgetPercent),
      },
    },
    sapUsages: asArray(result.sapGroupBudgetTotal).map((raw) => {
      const item = asRecord(raw) ?? {};
      return {
        name: asText(item.sapAccount) || '-',
        total: toAmount(item.total),
        curr: toAmount(item.curr),
        percentText: formatUsagePercent(Number(item.percent ?? 0)),
      };
    }),
    budgetChecks: asArray(result.budgetData).map((raw) => {
      const budget = asRecord(raw) ?? {};
      const type = asText(budget.type);
      // 같은 계정이 여러 그룹이면 마지막 값(레거시 forEach 덮어쓰기)
      let currentUsage: MarketingAmount = 0;
      groupsRaw.forEach((g) => {
        if (g.sapAccount === budget.type) currentUsage = toAmount(g.totalCost);
      });
      return {
        type,
        budgetTotal: toAmount(budget.BudgetTotl),
        remain: toAmount(budget.Remain),
        currentUsage,
        proceedPercent: toNumber(budget.proceedPercent),
      };
    }),
  };
};

/** 레거시 Number.toLocaleString() — 한국어 브라우저 표기로 고정 */
export const formatMarketingAmount = (n: MarketingAmount): string =>
  typeof n === 'string'
    ? n
    : (Number.isFinite(n) ? n : 0).toLocaleString('ko-KR');

export const checkMarketingExpenseBeforeSave = (
  state: MarketingExpenseState | null | undefined,
): string | null =>
  state?.analysisId && state.analysis
    ? null
    : '마케팅 분석 결과를 불러오지 못했습니다.';

/** 편집 — 저장 attributes.analysisId 로 분석 레코드를 다시 조회(레거시 getData) */
export const parseMarketingExpenseAttributes = (
  attributes: unknown,
): MarketingExpenseState | null => {
  const analysisId = asText(asRecord(attributes)?.analysisId);
  return analysisId ? { analysisId, analysis: null } : null;
};

/** data·internalOrderNumber 는 서버 소유 — 요청엔 analysisId·content 만 */
export const buildMarketingExpenseAttributes = (
  analysisId: string,
  content: string,
): ApprovalAttributes => ({ analysisId, content });

/** 조회 화면 상신 — 단건조회 attributes 에서 요청 가능한 값만 */
export const pickMarketingExpenseRequestAttributes = (
  attributes: unknown,
): ApprovalAttributes | null => {
  const a = asRecord(attributes);
  if (!a) return null;
  return buildMarketingExpenseAttributes(
    asText(a.analysisId),
    asText(a.content),
  );
};

export const getMarketingInternalOrderNumber = (attributes: unknown): string =>
  asText(asRecord(attributes)?.internalOrderNumber).trim();
